import type { MbcBriefAssetFile, MbcBriefAssetLink } from "@nlc/shared-types";

/**
 * Uploaded file reference — the file lives in S3, metadata in the MySQL
 * `attachments` row (`attachmentId`). Mirrors `assetFileSchema` in the AI
 * module, which rejects an entry missing `attachmentId` or `url`.
 */
export type AssetFileRef = MbcBriefAssetFile;

/** A shared link (no upload needed). */
export type AssetLinkRef = MbcBriefAssetLink;

/** A group of assets — shared ("used in all variations") or per-variation. */
export type AssetGroup = {
  files: AssetFileRef[];
  links: AssetLinkRef[];
};

/** In-flight upload shown in the block until it resolves to an `AssetFileRef`. */
export type PendingUpload = {
  id: string;
  name: string;
  status: "error" | "uploading";
};

export const emptyGroup = (): AssetGroup => ({ files: [], links: [] });

export const groupCount = (group: AssetGroup): number =>
  group.files.length + group.links.length;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

/**
 * Reads an asset group out of the brief working memory.
 *
 * Entries the AI module's `assetFileSchema`/`assetLinkSchema` would reject are
 * discarded instead — they can never render or reach submit, and keeping one
 * would 400 every subsequent save rather than just itself.
 */
export function readGroup(raw: unknown): AssetGroup {
  const source = (raw ?? {}) as {
    files?: unknown;
    links?: unknown;
  };

  const files = (Array.isArray(source.files) ? source.files : [])
    .filter((entry): entry is Record<string, unknown> => isRecord(entry))
    .filter(
      (file) =>
        typeof file.attachmentId === "number" && typeof file.url === "string",
    )
    .map((file) => ({
      ...file,
      name: typeof file.name === "string" ? file.name : "file",
    })) as AssetGroup["files"];

  const links = (Array.isArray(source.links) ? source.links : [])
    .filter((entry): entry is Record<string, unknown> => isRecord(entry))
    .filter((link) => typeof link.url === "string")
    .map((link) => ({ url: link.url as string }));

  return { files, links };
}

/**
 * Unions two groups, `base` winning on conflict. Callers that append to the
 * brief re-read the stored group first, then merge by identity (`attachmentId`
 * for files, `url` for links) so later additions do not replace existing ones.
 */
export function mergeGroups(base: AssetGroup, extra: AssetGroup): AssetGroup {
  const seenFiles = new Set(base.files.map((file) => file.attachmentId));
  const seenLinks = new Set(base.links.map((link) => link.url));

  return {
    files: [
      ...base.files,
      ...extra.files.filter((file) => !seenFiles.has(file.attachmentId)),
    ],
    links: [
      ...base.links,
      ...extra.links.filter((link) => !seenLinks.has(link.url)),
    ],
  };
}
